import { Loader2, RefreshCw, Send, Sparkles } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import type { MappingMessage, StructuredIngestTable } from '@/services/api/structuredIngest'
import { cn } from '@/utils/cn'

interface ConversationState {
  table: StructuredIngestTable
  busy: boolean
  disabled: boolean
}

export function MappingConversation({
  state,
  onSend,
}: {
  state: ConversationState
  onSend: (message: string) => Promise<boolean>
}) {
  const { t } = useTranslation()
  const messages = conversationMessages(state.table)
  const log = useRef<HTMLDivElement>(null)
  const lastMessage = messages.at(-1)?.id
  useEffect(() => {
    if ((lastMessage || state.busy) && log.current) log.current.scrollTop = log.current.scrollHeight
  }, [lastMessage, state.busy])
  return (
    <section
      aria-label={t('tableMapping.assistant.title')}
      className="flex max-h-[75dvh] min-h-96 min-w-0 flex-col lg:h-full lg:max-h-none lg:min-h-0"
    >
      <div className="flex items-center justify-between gap-2 border-b px-4 py-3 max-md:flex-wrap">
        <h3 className="flex items-center gap-2 text-sm font-semibold max-md:whitespace-nowrap">
          <Sparkles className="size-4 text-primary" /> {t('tableMapping.assistant.title')}
        </h3>
        <Button
          variant="ghost"
          size="sm"
          className="max-md:ml-auto"
          disabled={state.disabled}
          onClick={() => void onSend('')}
        >
          <RefreshCw className="size-3.5" /> {t('tableMapping.assistant.redoTable')}
        </Button>
      </div>
      <div
        ref={log}
        role="log"
        aria-label={t('tableMapping.assistant.log')}
        className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4"
      >
        {messages.length ? (
          messages.map(item => <ConversationMessage key={item.id} message={item} />)
        ) : (
          <p className="text-sm text-muted-foreground">{t('tableMapping.assistant.empty')}</p>
        )}
        {state.busy && (
          <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin motion-reduce:animate-none" />{' '}
            {t('tableMapping.assistant.working')}
          </p>
        )}
        {state.table.error && !state.busy && (
          <p role="alert" className="text-sm text-destructive">
            {t('tableMapping.assistant.failed', { error: state.table.error })}
          </p>
        )}
      </div>
      <MappingComposer disabled={state.disabled} onSend={onSend} />
    </section>
  )
}

function conversationMessages(table: StructuredIngestTable): MappingMessage[] {
  if (table.conversation?.length) return table.conversation
  if (!table.proposal.plain_summary) return []
  return [
    {
      id: 'initial-proposal',
      role: 'assistant',
      content: table.proposal.plain_summary,
      createdAt: '',
    },
  ]
}

function ConversationMessage({ message }: { message: MappingMessage }) {
  const { t } = useTranslation()
  return (
    <div
      className={cn(
        'max-w-prose text-sm',
        message.role === 'user' && 'ml-6 rounded-lg bg-muted p-3'
      )}
    >
      <p className="mb-1 text-xs font-medium text-muted-foreground">
        {message.role === 'user'
          ? t('tableMapping.assistant.you')
          : t('tableMapping.assistant.title')}
      </p>
      <p className="whitespace-pre-wrap break-words leading-relaxed">{message.content}</p>
    </div>
  )
}

function MappingComposer({
  disabled,
  onSend,
}: {
  disabled: boolean
  onSend: (message: string) => Promise<boolean>
}) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const submit = async () => {
    const message = draft.trim()
    if (!message || disabled || sending) return
    setSending(true)
    setError(null)
    try {
      if (await onSend(message)) setDraft('')
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : t('tableMapping.assistant.sendFailed'))
    } finally {
      setSending(false)
    }
  }
  return (
    <form
      className="space-y-2 border-t p-3"
      onSubmit={event => {
        event.preventDefault()
        void submit()
      }}
    >
      <Textarea
        aria-label={t('tableMapping.assistant.composer')}
        value={draft}
        maxLength={12000}
        onChange={event => setDraft(event.target.value)}
        placeholder={t('tableMapping.assistant.placeholder')}
        rows={3}
        disabled={disabled || sending}
        onKeyDown={event => {
          if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
            event.preventDefault()
            void submit()
          }
        }}
      />
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">{t('tableMapping.assistant.note')}</span>
        <Button
          type="submit"
          size="sm"
          disabled={disabled || sending || !draft.trim()}
          aria-label={t('tableMapping.assistant.sendLabel')}
        >
          <Send className="size-4" /> {t('tableMapping.assistant.send')}
        </Button>
      </div>
      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </form>
  )
}

export function MappingProgress({ filename }: { filename: string }) {
  const { t } = useTranslation()
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-72 flex-1 flex-col items-center justify-center gap-4 p-8 text-center"
    >
      <Loader2 className="size-7 animate-spin text-primary motion-reduce:animate-none" />
      <div className="max-w-sm space-y-2">
        <p className="font-medium">{t('tableMapping.assistant.progressTitle')}</p>
        <p className="text-sm text-muted-foreground">
          {t('tableMapping.assistant.progressBody', { file: filename })}
        </p>
      </div>
    </div>
  )
}
